<template>
    <div>
        <Bar :data="chartData" :options="chartOptions" />
    </div>
</template>

<script>
import {
    Chart as ChartJS,
    BarElement,
    CategoryScale,
    LinearScale,
    Tooltip,
    Legend
} from 'chart.js';
import { Bar } from 'vue-chartjs';
import moment from 'moment';
import { hexToRgbA } from '../utils/helper';

// Register only necessary components from Chart.js
ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

export default {
    name: 'user-barchart',
    components: {
        Bar
    },
    props: {
        statistics: Object,
        filterVehicle: String,
        filterBrand: String,
        filterYear: Array
    },
    data() {
        return {
            chartOptions: {
                aspectRatio: 1,
                plugins: {
                    tooltip: {
                        backgroundColor: '#707070',
                        bodyFont: {
                            family: 'Roboto',
                            size: 11
                        },
                        bodySpacing: 10,
                        borderColor: '#707070',
                        padding: 10,
                        cornerRadius: 4,
                        displayColors: false,
                        mode: 'index',
                        position: 'nearest',
                        titleFont: {
                            family: 'Roboto',
                            size: 13
                        },
                        titleMarginBottom: 20
                    },
                    legend: {
                        align: 'start',
                        labels: {
                            color: 'white',
                            font: {
                                family: 'Roboto',
                                size: 8
                            },
                            padding: 10,
                            boxWidth: 20
                        },
                        position: 'bottom',
                        onClick: e => e.stopPropagation()
                    }
                },
                maintainAspectRatio: false,
                scales: {
                    x: {
                        ticks: {
                            color: 'white',
                            font: {
                                family: 'Roboto',
                                size: 8
                            },
                            beginAtZero: true,
                            callback: this.labelFormatter
                        },
                        stacked: true,
                        grid: {
                            color: 'transparent'
                        }
                    },
                    y: {
                        ticks: {
                            color: 'white',
                            font: {
                                family: 'Roboto',
                                size: 8
                            },
                            beginAtZero: true,
                            callback: this.labelFormatter
                        },
                        stacked: true,
                        grid: {
                            color: 'transparent'
                        }
                    }
                }
            }
        };
    },
    methods: {
        labelFormatter(label) {
            return Math.floor(label) === label ? label : null;
        }
    },
    computed: {
        chartData() {
            const datasets = [];

            this.statistics.forEach((year, index) => {
                if (
                    this.filterYear.length > 0 &&
                    !this.filterYear.includes(index)
                )
                    return;

                const cuvColor = hexToRgbA(
                    year.cuv.color,
                    (index + 1) / this.statistics.length
                );
                const wwColor = hexToRgbA(
                    year.ww.color,
                    (index + 1) / this.statistics.length
                );

                // CUV brands
                year.cuv.brands.forEach(brand => {
                    if (
                        this.filterBrand &&
                        brand.name.toLowerCase() !== this.filterBrand
                    )
                        return;
                    const colors = brand.months.map(() => cuvColor);

                    datasets.push({
                        stack: year.name,
                        label: `${this.$vuetify.lang.t(
                            '$vuetify.page_statistics_detail.modelcategory.cuv'
                        )} ${year.name}`,
                        data: brand.months,
                        backgroundColor: colors,
                        borderColor: colors
                    });
                });

                // WW brands
                year.ww.brands.forEach(brand => {
                    if (
                        this.filterBrand &&
                        brand.name.toLowerCase() !== this.filterBrand
                    )
                        return;
                    const colors = brand.months.map(() => wwColor);

                    datasets.push({
                        stack: year.name,
                        label: `${this.$vuetify.lang.t(
                            '$vuetify.page_statistics_detail.modelcategory.ww'
                        )} ${year.name}`,
                        data: brand.months,
                        backgroundColor: colors,
                        borderColor: colors
                    });
                });
            });

            return {
                labels: Array.from({ length: 12 }, (_, i) =>
                    moment()
                        .locale(this.$i18n.locale)
                        .month(i)
                        .format('MMM')
                ),
                datasets
            };
        }
    },
    watch: {
        filterVehicle() {
            this.$nextTick(() => {
                this.chartData = this.getChartData();
            });
        },
        filterBrand() {
            this.$nextTick(() => {
                this.chartData = this.getChartData();
            });
        },
        filterYear() {
            this.$nextTick(() => {
                this.chartData = this.getChartData();
            });
        }
    },
    mounted() {
        this.$nextTick(() => {
            this.chartData = this.getChartData();
        });
    }
};
</script>
